import { useRef, useState } from "react";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, X } from "@phosphor-icons/react";

// File uploader (images or videos). value = stored ref string (URL or storage path).
// onChange receives the new value. Accepts pasted URLs as fallback.
export default function FileUploader({ label = "Upload", value, onChange, accept = "image/*", testid = "uploader" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed";
      if (e?.response?.status === 503) {
        toast.error("Uploads disabled — paste a URL instead.");
        setPasteMode(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={`${testid}-root`}>
      <div className="text-xs uppercase tracking-widest text-dim mb-1">{label}</div>
      <div className="flex gap-2">
        {!pasteMode ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`${testid}-input`}
            />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="btn-ghost inline-flex items-center gap-2" data-testid={`${testid}-pick`}>
              <UploadSimple size={16} weight="bold" /> {uploading ? "Uploading..." : (value ? "Replace" : "Choose File")}
            </button>
            <button type="button" onClick={() => setPasteMode(true)} className="text-dim text-xs underline" data-testid={`${testid}-paste-toggle`}>
              or paste URL
            </button>
          </>
        ) : (
          <>
            <input
              type="text" placeholder="https://..." value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              className="flex-1 bg-black border border-white/15 px-3 py-2 focus:border-[#FF3B30] outline-none"
              data-testid={`${testid}-url`}
            />
            <button type="button" onClick={() => setPasteMode(false)} className="text-dim text-xs underline" data-testid={`${testid}-pick-toggle`}>
              use file
            </button>
          </>
        )}
      </div>

      {value && (
        <div className="mt-2 relative inline-block" data-testid={`${testid}-preview`}>
          {accept.includes("video") ? (
            <video src={resolveImage(value)} className="w-40 h-24 object-cover border border-white/10" muted />
          ) : (
            <img src={resolveImage(value)} alt="" className="w-32 h-32 object-cover border border-white/10" />
          )}
          <button type="button" onClick={() => onChange("")}
                  className="absolute -top-2 -right-2 bg-[#FF3B30] text-black w-6 h-6 flex items-center justify-center"
                  data-testid={`${testid}-clear`}>
            <X size={14} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}
